import { ReactNode, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Button } from 'components/ui/Button';
import { GenericLoading } from 'components/ui/Loader';
import { AppInitProvider } from 'shared/contexts/app-init';
import { PlateformeChoiceGate } from 'features/auth/PlateformeChoiceGate';
import { AUTH_UNAUTHORIZED_EVENT } from 'core/constants/AuthEvents';
import { signinSilentSingleFlight } from 'features/auth/singleFlightSilentRenew';
import { clearAccessToken, setAccessToken } from 'core/auth/accessTokenStorage';

interface RouteGuardProps {
  requiresAuth?: boolean;
  children: ReactNode;
}

export const RouteGuard = ({
  requiresAuth = true,
  children
}: RouteGuardProps) => {
  const auth = useAuth();
  const location = useLocation();

  const [authError, setAuthError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isHandlingUnauthorized = useRef(false);
  const isRedirectingRef = useRef(false);

  // Redirect to login
  const handleLoginRedirect = useCallback(async () => {
    if (isRedirectingRef.current) return;
    isRedirectingRef.current = true;
    setIsRedirecting(true);

    try {
      sessionStorage.setItem('redirectUrl', location.pathname + location.search);
      await auth.signinRedirect();
    } catch (error) {
      console.error('Signin redirect error:', error);
      setAuthError('Votre session a expiré. Veuillez vous reconnecter.');
      isRedirectingRef.current = false;
      setIsRedirecting(false);
    }
  }, [auth, location]);

  // Central handler for 401 responses — runs ONCE, then stops
  useEffect(() => {
    const handleUnauthorized = async () => {
      if (isHandlingUnauthorized.current) return;
      isHandlingUnauthorized.current = true;

      // Clear token immediately to stop further API calls from triggering 401s
      clearAccessToken();

      try {
        // Single-flight : un seul renew concurrent (rafales de tokens observées en prod)
        const user = await signinSilentSingleFlight(auth);
        if (user?.access_token) {
          setAccessToken(user.access_token);
          // Success — allow future 401s to be handled again
          isHandlingUnauthorized.current = false;
        } else {
          handleLoginRedirect();
        }
      } catch {
        // Silent renewal failed — redirect to login, keep flag locked
        handleLoginRedirect();
      }
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);

    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, [auth, handleLoginRedirect]);

  // Handle auth state changes (token sync is done by AbpProvider)
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      setAuthError(null);
      isRedirectingRef.current = false;
      setIsRedirecting(false);
      isHandlingUnauthorized.current = false;
    } else if (!auth.isLoading && !auth.isAuthenticated) {
      clearAccessToken();
    }

    if (auth.error && !auth.error.message?.includes('Failed to fetch')) {
      console.error('OIDC Auth error:', auth.error);
      setAuthError('Votre session a expiré. Veuillez vous reconnecter.');
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.user, auth.error]);

  const handleManualRetry = () => {
    setAuthError(null);
    isRedirectingRef.current = false;
    setIsRedirecting(false);
    isHandlingUnauthorized.current = false;

    if (requiresAuth && !auth.isAuthenticated) {
      handleLoginRedirect();
    } else {
      window.location.reload();
    }
  };

  if (auth.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <GenericLoading loading isParentSized/>
        <p className="ml-4">Chargement de la session...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="text-red-600 text-center mb-6">
          <h2 className="text-xl font-bold mb-2">Session expirée</h2>
          <p className="mb-4">{authError}</p>
        </div>
        <div className="flex gap-3">
          <Button
            color='primary'
            onClick={handleManualRetry}
          >
            Se reconnecter
          </Button>
          <Button
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              window.location.href = '/';
            }}
            color="secondary"
          >
            Nouvelle session
          </Button>
        </div>
      </div>
    );
  }

  if (isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <GenericLoading loading isParentSized />
        </div>
      </div>
    );
  }

  if (requiresAuth && !auth.isAuthenticated && !auth.error) {
    return <RedirectToLogin onRedirect={handleLoginRedirect} />;
  }

  return (
    <AppInitProvider>
      <PlateformeChoiceGate>{children}</PlateformeChoiceGate>
    </AppInitProvider>
  );
};

const RedirectToLogin = ({ onRedirect }: { onRedirect: () => void }) => {
  useEffect(() => {
    onRedirect();
  }, [onRedirect]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <GenericLoading loading isParentSized />
    </div>
  );
};
